import {
  AppManagerCommands,
  type AppVisibilityMode,
  IPCEventType,
  MainWindowCommands,
  type MainWindowNavigateEvent,
  type Theme,
} from '@electron/shared';
import { type BaseEvent, createEventBus } from '@littlebird/event-bus';

import { type FileRoutesByTo } from '@/routeTree.gen';
import { type RouteParams, type RouteSearch } from '@/types/routes';

import { ElectronAPI } from '../base/ElectronAPI';
import { type ElectronListenerMethod } from '../base/types';

export const WINDOW_API_READY = 'WINDOW_API_READY';
export const MAIN_WINDOW_ENTER_FULLSCREEN = 'MAIN_WINDOW_ENTER_FULLSCREEN';
export const MAIN_WINDOW_LEAVE_FULLSCREEN = 'MAIN_WINDOW_LEAVE_FULLSCREEN';

export interface WindowEventsRegistry {
  [WINDOW_API_READY]: undefined;
  [MAIN_WINDOW_ENTER_FULLSCREEN]: undefined;
  [MAIN_WINDOW_LEAVE_FULLSCREEN]: undefined;
}

type WindowEventMap = {
  [K in keyof WindowEventsRegistry]: BaseEvent & {
    type: K;
    data: WindowEventsRegistry[K];
  };
};

/**
 * Window APIs for Electron
 */

export class WindowAPI extends ElectronAPI {
  static event = createEventBus<WindowEventMap>({ name: 'WindowAPI' });

  constructor() {
    super();

    this.listenForMainWindowEnterFullScreen(() =>
      WindowAPI.event.emit(MAIN_WINDOW_ENTER_FULLSCREEN, {
        data: undefined,
      })
    );

    this.listenForMainWindowLeaveFullScreen(() => {
      WindowAPI.event.emit(MAIN_WINDOW_LEAVE_FULLSCREEN, {
        data: undefined,
      });
    });

    setTimeout(() => {
      WindowAPI.event.emit(WINDOW_API_READY, {
        data: undefined,
      });
    }, 0);
  }
  /**
   * Focus the main window
   */
  async focusMainWindow() {
    return this.invoke<void>('focus-main-window');
  }

  /** Brings the main window in front of other apps, without a user action */
  async bringMainWindowToFront() {
    return this.invoke<void>('bring-main-window-to-front');
  }

  async blurMainWindow() {
    return this.invoke<void>('blur-main-window');
  }

  async setWindowButtonPosition(pos: { x: number; y: number }) {
    return this.invoke<void>('set-window-button-position', pos);
  }

  private backgroundTimerClaims = 0;

  keepTimersRunningInBackground() {
    if (this.backgroundTimerClaims++ === 0) {
      this.setBackgroundThrottling(false);
    }
    let released = false;
    return () => {
      if (released) return;
      released = true;
      if (--this.backgroundTimerClaims === 0) {
        this.setBackgroundThrottling(true);
      }
    };
  }

  private setBackgroundThrottling(throttle: boolean) {
    this.invoke<void>(
      MainWindowCommands.SetBackgroundThrottling,
      throttle
    ).catch((error) => {
      console.warn('[WindowAPI] Failed to set background throttling', error);
    });
  }

  async setTitleBarOverlayTheme(theme: Theme) {
    return this.invoke<void>(MainWindowCommands.SetTitleBarOverlayTheme, {
      theme,
    });
  }

  async getAppVisibilityMode() {
    return this.invoke<AppVisibilityMode>(
      AppManagerCommands.GetAppVisibilityMode
    );
  }

  async setAppVisibilityMode(mode: AppVisibilityMode) {
    return this.invoke<void>(AppManagerCommands.SetAppVisibilityMode, {
      mode,
    });
  }

  /**
   * Get mouse position relative to the current window
   * Returns x, y coordinates in window space
   */
  async getMousePositionInWindow(): Promise<{ x: number; y: number }> {
    return this.invoke<{ x: number; y: number }>(
      'get-mouse-position-in-window'
    );
  }

  async getCursorScreenPoint(): Promise<{ x: number; y: number }> {
    return this.invoke<{ x: number; y: number }>('get-cursor-screen-point');
  }

  /**
   * Set whether the window should ignore mouse events
   * @param ignore - Whether to ignore mouse events
   * @param options - Optional settings. Set forward: true to still receive mouse move events for hover effects
   */
  async setIgnoreMouseEvents(
    ignore: boolean,
    options?: { forward?: boolean }
  ): Promise<void> {
    return this.invoke<void>('set-ignore-mouse-events', { ignore, options });
  }

  /**
   * Navigate the main window to a specific route and focus it
   *
   * This sends a command to the main process to navigate the main window to a specific route.
   * The main window will be shown and focused if hidden.
   * With `showInactive`, it is shown above the active app without taking the
   * keyboard focus.
   *
   * @param route - The route path to navigate to (type-safe)
   * @param options - Navigation options
   * @param options.params - Path parameters (e.g., chatId for /chats/$chatId)
   * @param options.search - Query/search parameters
   * @param options.hash - Hash parameter for settings modal navigation (e.g., 'settings/Integrations')
   *
   * @example
   * ```typescript
   * // Navigate with path params
   * await electronApis.window.mainWindowNavigateTo('/chats/$chatId', {
   *   params: { chatId: '123' }
   * });
   *
   * // Navigate with search params
   * await electronApis.window.mainWindowNavigateTo('/meetings', {
   *   search: { filter: 'upcoming' }
   * });
   *
   * // Navigate with both
   * await electronApis.window.mainWindowNavigateTo('/chats/$chatId', {
   *   params: { chatId: '123' },
   *   search: { highlight: 'message-456' }
   * });
   *
   * // Navigate with hash (for settings modal)
   * await electronApis.window.mainWindowNavigateTo('/', {
   *   hash: 'settings/Integrations'
   * });
   * ```
   */
  async mainWindowNavigateTo<T extends keyof FileRoutesByTo>(
    route: T,
    options?: {
      params?: RouteParams<T>;
      search?: RouteSearch<T>;
      hash?: string;
      newTab?: boolean;
      showInactive?: boolean;
    }
  ): Promise<void> {
    return this.invoke<void>(MainWindowCommands.NavigateToRoute, {
      route,
      params: options?.params,
      search: options?.search,
      hash: options?.hash,
      newTab: options?.newTab,
      showInactive: options?.showInactive,
    });
  }

  /**
   * Listen for navigation requests from the main process
   *
   * This allows the renderer to respond when the main process requests navigation
   * to a specific route (e.g., from notifications, deep links, or other windows).
   *
   * @param callback - Function called when navigation is requested with route and search params
   * @param onError - Optional error handler
   * @param onUnlisten - Optional cleanup handler called when listener is removed
   * @param timeout - Optional timeout in ms to auto-remove listener
   * @returns Promise that resolves to unsubscribe function
   *
   * @example
   * ```typescript
   * const unsubscribe = await electronApis.window.mainWindowListenForNavigation(
   *   ({ route, search }) => {
   *     router.navigate({ to: route as any, search });
   *   }
   * );
   *
   * // Later, to stop listening:
   * unsubscribe();
   * ```
   */
  mainWindowListenForNavigation: ElectronListenerMethod<IPCEventType.mainWindowNavigateToRoute> =
    (callback, onError, onUnlisten, timeout) =>
      this.addListener<MainWindowNavigateEvent>({
        eventType: IPCEventType.mainWindowNavigateToRoute,
        callback,
        onError,
        onUnlisten,
        timeout,
      });

  async minimizeWindow() {
    return this.invoke<void>(MainWindowCommands.Minimize);
  }

  async maximizeWindow() {
    return this.invoke<void>(MainWindowCommands.Maximize);
  }

  async closeWindow() {
    return this.invoke<void>(MainWindowCommands.Close);
  }

  async isWindowMaximized(): Promise<boolean> {
    return this.invoke<boolean>(MainWindowCommands.IsMaximized);
  }

  async getMainWindowFullScreenState(): Promise<boolean> {
    return this.invoke<boolean>('get-main-window-fullscreen-state');
  }

  private listenForMainWindowEnterFullScreen: ElectronListenerMethod<IPCEventType.mainWindowEnterFullScreen> =
    (callback, onError, onUnlisten, timeout) =>
      this.addListener({
        eventType: IPCEventType.mainWindowEnterFullScreen,
        callback,
        onError,
        onUnlisten,
        timeout,
      });

  private listenForMainWindowLeaveFullScreen: ElectronListenerMethod<IPCEventType.mainWindowLeaveFullScreen> =
    (callback, onError, onUnlisten, timeout) =>
      this.addListener({
        eventType: IPCEventType.mainWindowLeaveFullScreen,
        callback,
        onError,
        onUnlisten,
        timeout,
      });
}
